import { useEffect, useRef, useState } from "react";
import { Mic, Send, Plus, Sparkles, Check, StopCircle } from "lucide-react";
import ProvenanceTag from "@/components/system/ProvenanceTag";
import Ni2Icon from "@/components/system/Ni2Icon";

/**
 * VoiceInput — the conversational surface for Ask NeuroNeeti.
 * States: idle → listening → thinking → answer.
 * Fully client-side simulation for the fellowship demo; no external services.
 */

const TRANSCRIPT_STEPS = [
  "Show me every case where I left a ",
  "Show me every case where I left a dural tail ",
  "Show me every case where I left a dural tail and what ",
  "Show me every case where I left a dural tail and what happened at six-month follow-up.",
];

const THINKING_CHECKS = [
  { label: "Cases found",           value: "4 similar cases",             delay: 700  },
  { label: "Reflections found",     value: "9 reflections linked",        delay: 1400 },
  { label: "Operative pearls",      value: "3 pearls surfaced",           delay: 2100 },
  { label: "Literature linked",     value: "Simpson 1957 · Yaşargil 1996", delay: 2800 },
];

const ANSWER = {
  text:
    "Across your last four cases with a residual dural tail, none progressed on six-month MRI. Two showed stable enhancement, one regressed after Gamma Knife, and one is due for imaging next week.",
  provenance: [
    { kind: "amber", label: "Case",       ref: "NN-2039-B" },
    { kind: "amber", label: "Case",       ref: "NN-2034-F" },
    { kind: "amber", label: "Reflection", ref: "MEM-3812"  },
    { kind: "blue",  label: "Paper",      ref: "Simpson · 1957" },
    { kind: "blue",  label: "Paper",      ref: "Yaşargil · 1996" },
  ],
};

export default function VoiceInput({ testId }) {
  const [mode, setMode] = useState("idle"); // idle | listening | thinking | answer
  const [transcript, setTranscript] = useState("");
  const [revealed, setRevealed] = useState(0); // # of checks revealed
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  const startListening = () => {
    clearTimers();
    setMode("listening");
    setTranscript("");
    setRevealed(0);
    TRANSCRIPT_STEPS.forEach((t, i) => {
      const id = setTimeout(() => setTranscript(t), 550 * (i + 1));
      timersRef.current.push(id);
    });
    const stopId = setTimeout(() => startThinking(TRANSCRIPT_STEPS[TRANSCRIPT_STEPS.length - 1]), 550 * (TRANSCRIPT_STEPS.length + 1) + 400);
    timersRef.current.push(stopId);
  };

  const stopListening = () => {
    clearTimers();
    setMode("idle");
    setTranscript("");
  };

  const startThinking = (t) => {
    clearTimers();
    setTranscript(t || transcript);
    setMode("thinking");
    setRevealed(0);
    THINKING_CHECKS.forEach((c, i) => {
      const id = setTimeout(() => setRevealed(i + 1), c.delay);
      timersRef.current.push(id);
    });
    const finalId = setTimeout(() => setMode("answer"), THINKING_CHECKS[THINKING_CHECKS.length - 1].delay + 700);
    timersRef.current.push(finalId);
  };

  const reset = () => {
    clearTimers();
    setMode("idle");
    setTranscript("");
    setRevealed(0);
  };

  const isListening = mode === "listening";
  const isThinking = mode === "thinking";
  const isAnswer = mode === "answer";

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      {/* Live conversation surface (transcript / thinking / answer) */}
      {(isListening || isThinking || isAnswer) && (
        <div className="nn-glass-inset nn-enter flex flex-col gap-3 p-4">
          {/* Surgeon turn */}
          <div className="flex items-start gap-3">
            <span className="mt-1 grid h-6 w-6 place-items-center rounded-full bg-gradient-to-br from-[#5b86c7] to-[#213d69] text-[10.5px] font-semibold text-white/90">
              MR
            </span>
            <div className="min-w-0 flex-1">
              <div className="nn-eyebrow mb-1">Dr. Rao</div>
              <div className="text-[14px] leading-[22px] text-[hsl(var(--nn-ink))]">
                {transcript || <span className="text-[hsl(var(--nn-ink-faint))]">…</span>}
                {isListening && (
                  <span className="ml-0.5 inline-block h-4 w-[2px] translate-y-[3px] animate-pulse bg-[hsl(var(--nn-blue))]" />
                )}
              </div>
            </div>
          </div>

          {/* System turn */}
          {(isThinking || isAnswer) && (
            <div className="flex items-start gap-3">
              <span className="mt-0.5">
                <Ni2Icon size={22} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="nn-eyebrow mb-1">Ni²</div>

                {isThinking && (
                  <div className="flex flex-col gap-1.5">
                    <div className="text-[13.5px] italic text-[hsl(var(--nn-ink-muted))]">
                      Searching your surgical memory…
                    </div>
                    <ul className="mt-1 flex flex-col gap-1">
                      {THINKING_CHECKS.map((c, i) => (
                        <li
                          key={c.label}
                          className="flex items-center gap-2 text-[12.5px]"
                          style={{
                            opacity: i < revealed ? 1 : 0.3,
                            transform: i < revealed ? "translateX(0)" : "translateX(-4px)",
                            transition: "opacity 280ms ease, transform 280ms ease",
                          }}
                        >
                          <span
                            className="grid h-4 w-4 place-items-center rounded-full"
                            style={{
                              background:
                                i < revealed ? "rgba(130, 210, 170, 0.18)" : "rgba(255,255,255,0.04)",
                              border:
                                i < revealed
                                  ? "1px solid rgba(130, 210, 170, 0.4)"
                                  : "1px solid rgba(255,255,255,0.08)",
                            }}
                          >
                            {i < revealed && (
                              <Check className="h-2.5 w-2.5 text-[hsl(var(--nn-ok))]" strokeWidth={3} />
                            )}
                          </span>
                          <span className="text-[hsl(var(--nn-ink))]">{c.label}</span>
                          <span className="text-[hsl(var(--nn-ink-faint))]">·</span>
                          <span className="text-[hsl(var(--nn-ink-muted))]">{c.value}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {isAnswer && (
                  <div className="flex flex-col gap-2">
                    <p className="text-[14px] leading-[22px] text-[hsl(var(--nn-ink))]">
                      {ANSWER.text}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      {ANSWER.provenance.map((p, i) => (
                        <span
                          key={`${p.label}-${p.ref}`}
                          className="nn-prov-in"
                          style={{ animationDelay: `${i * 70}ms` }}
                        >
                          <ProvenanceTag kind={p.kind} label={p.label} ref={p.ref} />
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Composer */}
      <div
        className={`nn-glass-inset flex items-center gap-2 p-2 pl-3 transition-all duration-300 ${
          isListening ? "nn-listen-glow" : ""
        }`}
      >
        <button className="nn-icon-btn shrink-0" aria-label="Attach" title="Attach">
          <Plus className="h-4 w-4" strokeWidth={1.6} />
        </button>

        {isListening ? (
          <div className="flex flex-1 items-center gap-3 px-1">
            <Waveform />
            <div className="flex flex-col leading-tight">
              <span className="text-[12.5px] font-medium text-[hsl(var(--nn-ink))]">
                Listening…
              </span>
              <span className="text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
                Streaming transcript live
              </span>
            </div>
          </div>
        ) : (
          <input
            className="flex-1 bg-transparent px-1 py-2 text-[13.5px] text-[hsl(var(--nn-ink))] placeholder:text-[hsl(var(--nn-ink-faint))] focus:outline-none"
            placeholder={
              isThinking
                ? "Searching your surgical memory…"
                : isAnswer
                  ? "Ask a follow-up…"
                  : "Ask across your cases, reflections, imaging, literature…"
            }
            readOnly={isThinking}
          />
        )}

        {isListening ? (
          <button
            className="nn-icon-btn shrink-0"
            aria-label="Stop"
            title="Stop listening"
            onClick={stopListening}
            style={{ color: "hsl(var(--nn-amber))" }}
          >
            <StopCircle className="h-4 w-4" strokeWidth={1.8} />
          </button>
        ) : (
          <button
            data-testid="voice-input-mic"
            className="nn-icon-btn shrink-0"
            aria-label="Voice"
            title="Voice input"
            onClick={startListening}
          >
            <Mic className="h-4 w-4" strokeWidth={1.6} />
          </button>
        )}

        <button
          data-testid="voice-input-send"
          className="nn-icon-btn shrink-0"
          aria-label="Send"
          title="Send"
          onClick={() => (isAnswer ? reset() : startThinking())}
          style={{
            background:
              "linear-gradient(180deg, rgba(160, 200, 255, 0.28), rgba(120, 165, 240, 0.18))",
            borderColor: "rgba(160, 200, 255, 0.4)",
            color: "hsl(var(--nn-ink))",
          }}
        >
          <Send className="h-4 w-4" strokeWidth={1.6} />
        </button>
      </div>

      {/* Suggestions row */}
      {mode === "idle" && (
        <div className="flex flex-wrap gap-1.5">
          {[
            "Prep me for R.K.'s schwannoma",
            "Compare my last 3 aneurysm clippings",
            "Reflections tagged CN VII",
            "M&M candidates this month",
          ].map((s) => (
            <button
              key={s}
              className="nn-chip transition-colors hover:text-[hsl(var(--nn-ink))]"
              onClick={startListening}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Waveform bars ---------- */
function Waveform() {
  const bars = [22, 55, 90, 65, 40, 78, 95, 50, 30, 70, 55, 35, 80, 45, 60, 92, 42, 25];
  return (
    <div className="nn-wave" aria-hidden="true">
      {bars.map((h, i) => (
        <span
          key={i}
          style={{
            height: `${h}%`,
            animationDelay: `${(i % 6) * 90}ms`,
            animationDuration: `${900 + (i % 5) * 90}ms`,
          }}
        />
      ))}
    </div>
  );
}
